import React from "react";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { ButtonGroup, NumberInput, Select } from "@caelor/ui";
import { useTranslation } from "react-i18next";
import {
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  getPaginationOptions,
} from "./defaults";
import { useNewStartersStore } from "../../../../../../stores/use-new-starters-store";
import { useStyleguideColors } from "../../../../../../hooks/use-styleguide-colors";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import useCosmosFields from "../../../../../../hooks/use-cosmos-fields";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import { DEFAULT_ACCENT_COLOR } from "../../../../../../features/people/defaults";

function LayoutSettings() {
  const { t } = useTranslation();

  const { data, updateData } = useNewStartersStore((state) => ({
    data: state.data,
    updateData: state.updateData,
  }));

  const paginationOptions = getPaginationOptions(t);

  const { fields } = useCosmosFields();

  const { isCosmosInstalled, styleguideSetup } = useOpticsStore((state) => ({
    isCosmosInstalled: state.isCosmosInstalled,
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("display-options")}</SettingsOptionHeading>

        <OptionContainer>
          <OptionLabel>{t("pagination")}</OptionLabel>
          <OptionContent>
            <Select
              options={paginationOptions}
              value={paginationOptions.find(
                ({ value }) => value === data.pagination
              )}
              onChange={({ value }) =>
                updateData({ value: { pagination: value } })
              }
            />
          </OptionContent>
        </OptionContainer>

        <OptionContainer>
          <OptionLabel>{t("per-page")}</OptionLabel>
          <OptionContent>
            <NumberInput
              value={data.maxNumberOfItems}
              setValue={(value) => {
                updateData({ value: { maxNumberOfItems: value } });
              }}
              min={1}
              isFullWidth
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data?.openInNewTab ?? true}
              onClick={(isOn) =>
                updateData({
                  value: { openInNewTab: isOn },
                })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      {isCosmosInstalled && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>
            {t("flexible-display-items")}
          </SettingsOptionHeading>

          <OptionContainer direction="column">
            <OptionLabel>{t("subheader")}</OptionLabel>
            <OptionContent full>
              <Select
                value={fields.find(({ value }) => value === data.titleFieldId)}
                options={fields}
                onChange={(data) => {
                  const { value: titleFieldId } = data || {};
                  updateData({ value: { titleFieldId: titleFieldId || "" } });
                }}
                isClearable
                placeholder={t("select-subheader")}
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>

        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data.borderRadius}
              options={{ input: true }}
              onClick={(value) =>
                updateData({ value: { borderRadius: value } })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4) {
                  updateData({ value: { borderRadius: value } });
                }
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>

        {isCosmosInstalled && (
          <OptionContainer>
            <OptionLabel alignToTop>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                hideColorTiles
                value={{
                  accent:
                    data.accentColor ||
                    styleguideColors?.accent ||
                    DEFAULT_ACCENT_COLOR,
                }}
                onChange={({ accent }) => {
                  updateData({ value: { accentColor: accent } });
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
    </>
  );
}

export default LayoutSettings;
